<i18n locale="en">
{
  "headers": {
    "name": "Name",
    "createdAt": "Created On"
  },
  "loading": "Getting brands...",
  "no-data": "No brands",
  "no-query-results": "No brands match that search"
}
</i18n>

<template>
  <Responsive v-slot="{ width }" v-roles="['pb:*']">
    <OwnStack spacing="3">
      <SearchInput
        v-if="width < MOBILE_BREAKPOINT"
        ref="topSearch"
        v-model="requestParams.query"
        standard
      />
      <OwnTable
        :columns="columns"
        :items="brands"
        item-key="id"
        :query="requestParams.query"
        :card-breakpoint="MOBILE_BREAKPOINT"
        @click="navToBrand"
      >
        <template #loading>
          <DefaultComponentLoader v-if="isLoading" :message="$t('loading')" />
        </template>

        <template #no-data>
          <DefaultEmptyState :message="$t('no-data')">
            <template #icon>
              <PhStorefront size="24" />
            </template>
          </DefaultEmptyState>
        </template>

        <template #no-query-results>
          <DefaultEmptyState :message="$t('no-query-results')">
            <template #icon>
              <PhStorefront size="24" />
            </template>
          </DefaultEmptyState>
        </template>

        <template #heading.name>
          <SearchInput v-model="requestParams.query" borderless />
        </template>

        <template #item.name="{ item }">
          <BrandTableRow :item="item" />
        </template>

        <template #card="{ item }">
          <!-- @vue-expect-error OwnTable generic issue RES-1152 -->
          <BrandTableRow :item="item" />
        </template>
      </OwnTable>

      <TableFooter
        v-if="brands.length"
        :page="requestParams.page"
        :limit="requestParams.limit"
        :page-count="requestData.pageCount"
        :total-items="requestData.totalItems"
        :loading="isLoading"
        @prev="requestParams.page = requestParams.page - 1"
        @next="requestParams.page = requestParams.page + 1"
      />
    </OwnStack>
  </Responsive>
</template>

<script lang="ts" setup>
import { PhStorefront } from 'phosphor-vue/dist/phosphor-vue.esm'
import {
  ComponentPublicInstance,
  computed,
  onMounted,
  reactive,
  Ref,
  ref,
  watch,
} from 'vue'

import { useRouter } from 'vue-router/composables'

import { ConfiguredClient } from '@/api'
import DefaultEmptyState from '@/components/empty/DefaultEmptyState.vue'
import { SearchInput } from '@/components/inputs'
import { DefaultComponentLoader } from '@/components/loaders'
import { TableFooter } from '@/components/table'
import { PaginatedRequestData, PaginatedRequestParams, Brand } from '@/core'
import roles from '@/directives/roles'
import { logError } from '@/logger'
import { OwnStack, OwnTable, Responsive } from '@/ui'
import { focusInput } from '@/ui/utils'
import { useI18n } from '@/utils'

import { brandTableColumns } from './brandTableColumns'
import BrandTableRow from './BrandTableRow.vue'

const vRoles = roles

const MOBILE_BREAKPOINT = 600

const i18n = useI18n()
const router = useRouter()

const emit = defineEmits(['navigate'])

const isLoading = ref(false)
const brands = ref<Brand[]>([]) as Ref<Brand[]>

const topSearch = ref<ComponentPublicInstance | null>(null)

const requestParams = reactive<PaginatedRequestParams>({
  limit: 10,
  page: 1,
  query: undefined,
})

const requestData = reactive<PaginatedRequestData>({
  pageCount: undefined,
  totalItems: undefined,
})

const columns = computed(() => {
  return brandTableColumns.map((header) => ({
    ...header,
    label: i18n.$t(`headers.${header.key}`),
  }))
})

const loadBrands = async () => {
  try {
    const response = await ConfiguredClient.brands.getBrands({
      query: {
        limit: requestParams.limit,
        page: requestParams.page,
        query: requestParams.query !== '' ? requestParams.query : undefined,
      },
    })

    brands.value = response.results

    requestData.pageCount = response.pageCount
    requestData.totalItems = response.total
  } catch (err) {
    logError(err)
  }
}

const navToBrand = (item: Brand) => {
  router.push({
    name: 'brand.home',
    params: {
      brandId: item.id,
    },
  })

  emit('navigate')
}

watch(requestParams, () => {
  void loadBrands()
})

onMounted(() => {
  if (topSearch.value) {
    focusInput(topSearch.value)
  }

  void loadBrands()
})
</script>
